import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import * as _ from "lodash";
import { ToastrService } from "ngx-toastr";
import { forkJoin } from "rxjs";
import { Bot } from "../../../models/bot";
import { Role } from "../../../models/role";
import { BotControllerService } from "../../../services/role-controller/bot-controller.service";
import { RoleControllerService } from "../../../services/role-controller/role-controller.service";
import { MuleControllerService } from "../../../services/mule-controller/mule-controller.service";
import { RoleService } from "../../../services/role-controller/role.service";
import { FormControllerService } from "src/app/services/form-controller/form-controller.service";
import { Store } from "../../../services/store.service";
import { TranslatedBaseError } from "../TranslatedBaseError";
import { TranslateService } from "@ngx-translate/core";
import { MuleService } from "../../../models/mule-service";
import { Context } from "../../../models/context";
import { ServiceDetailComponent } from "../service-detail/service-detail.component";
import { map } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: "app-bots",
  templateUrl: "./bots.component.html",
  providers: [RoleControllerService, BotControllerService, FormControllerService],
})
export class BotsComponent extends TranslatedBaseError implements OnInit {

  @ViewChild("ServiceDetailComponent") public serviceDetailComponent: ServiceDetailComponent;

  @Input() public hideList: boolean = false;
  @Input() public bots: Bot[];
  @Input() public useServiceDetailWrapper = true;
  @Input() public botsSearch: any[];
  @Input() public botsByData: Bot[] = [];
  @Input() public botsByDoc: Bot[] = [];
  @Input() public botsByMessage: Bot[] = [];
  @Input() public botsInternal: Bot[] = [];
  @Input() public determinantMapById: { [key: string]: string };

  public roles: Role[];
  public menuBots: Role[];
  public activemenuBots: Role[];
  public orderedmenuBots: any[] = [];
  public serviceId: string;
  public baseUrl: string;
  public showBotAdd: boolean = false;
  public muleServices: MuleService[] = [];

  public allRoles: Role[] = [];
  public allFields: any[] = [];

  public tempBotsInternal: Bot[] = [];

  public subscription: any;
  public customFilterBy: any;
  public customFilter: any = {};
  public searchTextFilterObject: any;
  public hideSearch: boolean = false;
  public botNameField: string;
  public searchValue: string = "";

  public showCVFMapping: boolean = false;

  public context: Context = new Context();

  constructor(private roleController: RoleControllerService,
    private botController: BotControllerService,
    private roleService: RoleService,
    private formControllerService: FormControllerService,
    private router: Router,
    public store: Store,
    private muleService: MuleControllerService,
    private route: ActivatedRoute, toastr: ToastrService,
    public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.resetSearch();
    this.route.params.subscribe((params) => {
      this.serviceId = params.serviceId;
      this.baseUrl = "/services/" + this.serviceId + "/bots";
    });
    this.getBotsList();
    this.subscription = this.roleService.changed.subscribe((event) => {
      if (event.change) {
        this.getBotsList();
      }
    });
  }
  public onFilterBy(value) {
    this.customFilterBy = value;
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public getBotsList() {
    this.botController.getServiceBots(this.serviceId).subscribe(
      (data) => {
        this.bots = data;
        this.botsSearch = [];
        if (data.length) {
          data.forEach((bot) => {
            this.botsSearch.push(bot.name || bot.shortName);
          });
          data = _.sortBy(data, [(d) => (d.name || d.shortName).toLowerCase()], ["asc"]);
        }
        this.botsByData = data.filter((filter) => filter.botType === "data");
        this.botsByDoc = data.filter((filter) => filter.botType === "document");
        this.botsByMessage = data.filter((filter) => filter.botType === "message");
        this.botsInternal = _.sortBy(data.filter((filter) => filter.botType === "internal"), [(d) => (d.name || d.shortName).toLowerCase()], ["asc"]);
        const promises = [];
        this.botsInternal.forEach((b) => {
          promises.push(
            firstValueFrom(
              this.botController.getBot(b.id).pipe(
                map((fullbot) => {
                  return fullbot;
                })
              )
            )
          );
        });
        Promise.all<any[]>(promises).then((results: any[]) => {
          this.botsInternal.forEach((b) => {
            const newB = results.find((n) => n.id === b.id);
            if (newB) {
              b.inputFieldsMapping = newB.inputFieldsMapping;
              b.outputFieldsMapping = newB.outputFieldsMapping;
            }
          });
        });
        this.loadMuleServices();
        this.getRolesList();
        this.getActionsList();
      },
    );
  }

  public getRolesList() {
    this.roleController.getRolesDetailedList(this.serviceId).subscribe(
      (data) => {
        this.allRoles = data;
        this.updateRoleCounter();
      },
    );
  }

  public getActionsList() {
    this.formControllerService.getFlattenFormFieldsWithAdds(this.serviceId, false, true, false, false, false, true).subscribe(
      (data) => {
        this.allFields = data;
        this.updateActionCounter();
      },
    );
  }

  public updateActionCounter() {
    this.bots.forEach((b) => {
      b.counterActions = this.allFields.filter((r) => {
        return r.actions && r.actions.filter((a) => {
          return a.id === b.id;
        }).length;
      });
    });
  }

  public updateRoleCounter() {
    this.bots.forEach((b) => {
      b.counterRoles = this.allRoles.filter((r) => {
        return r.botRoleBots && r.botRoleBots.actions && r.botRoleBots.actions.find((a) => {
          return a.bots.find((ab) => {
            return ab.id === b.id;
          });
        });
      });
    });
  }

  public loadMuleServices(): void {
    this.muleService.getServicesList().subscribe(
      (data) => {
        this.muleServices = data;
        this.botsByData.forEach((bot) => {
          const existingDB = this.muleServices.find((d) => {
            return d.id === bot.botServiceId;
          });
          if (!existingDB) {
            bot.botServiceId = null;
          } else {
            bot.latestBotServiceId = this.checkVersion(bot);
            if (!bot.latestBotServiceId) {
              bot.latestBotService = this.getNewerVersion(bot);
              bot.currentBotService = this.getCurrentVerion(bot);
            }
          }
        });
        console.log(this.botsByData);
      },
      (err) => this.showErrorMessage(err),
    );
  }

  public checkVersion(bot) {
    if (!bot.botServiceId) {
      return false;
    }
    const services = this.muleServices.filter((s) => {
      return (this.getServiceNamePrefix(s.id) === this.getServiceNamePrefix(bot.botServiceId))
        && (this.getServiceNameSuffix(s.id) === this.getServiceNameSuffix(bot.botServiceId));
    });
    const newer = services.filter((s) => {
      return this.getServiceVersionNumber(s.id) > this.getServiceVersionNumber(bot.botServiceId);
    });
    return !newer.length;
  }

  public getCurrentVerion(bot) {
    const services = this.muleServices.filter((s) => {
      return s.id === bot.botServiceId;
    });
    return services[0].name;
  }

  public getNewerVersion(bot) {
    const services = this.muleServices.filter((s) => {
      return (this.getServiceNamePrefix(s.id) === this.getServiceNamePrefix(bot.botServiceId))
        && (this.getServiceNameSuffix(s.id) === this.getServiceNameSuffix(bot.botServiceId));
    });
    const newer = services.filter((s) => {
      return this.getServiceVersionNumber(s.id) > this.getServiceVersionNumber(bot.botServiceId);
    });
    return newer[0];
  }

  public getServiceNamePrefix(name) {
    const n = name ? name.split("(")[0] : "";
    return String(n);
  }

  public getServiceNameSuffix(name) {
    const n = name ? name.split(")")[name.split(")").length - 1] : "";
    return String(n);
  }

  public getServiceVersionNumber(name) {
    const endName = name.slice(name.lastIndexOf("("));
    const regExp = /\(([^)]+)\)/;
    const matches = regExp.exec(endName);
    return matches ? Number(matches[1].replace(".", "")) : 0;
  }

  public addedBot() {
    this.closeLayer();
    this.getBotsList();
  }

  public updateBot(role) {
    const successCb = (data) => {
      role.isInRenameMode = false;
      role.isLoading = false;
      this.responseSuccessful("Bot saved!");
      this.ngOnInit();
    };
    const errorCb = (error) => {
      this.ngOnInit();
      return this.showErrorMessageWithTitle("Error during save/update of bot!", error);
    };
    role.isFrontendBot = true;
    return this.botController.saveBot(role).subscribe(successCb, errorCb);
  }

  public updateRole(role) {
    const successCb = (data) => {
      role.isInRenameMode = false;
      role.isLoading = false;
      this.responseSuccessful("Bot saved!");
    };
    const errorCb = (error) => {
      this.ngOnInit();
      return this.showErrorMessageWithTitle("Error during save/update of bot!", error);
    };
    role.isFrontendBot = true;
    return this.roleController.saveBotRole(role).subscribe(successCb, errorCb);
  }

  // applicant shall be filtered out
  public setmenuBots(): void {
    this.menuBots = BotsComponent.filterUnnecessaryRoles(this.roles);
    this.menuBots = BotsComponent.filterFEBotRoles(this.menuBots);

    this.activemenuBots = this.menuBots;
    const all_obs = [];
    this.activemenuBots.forEach((role) => {
      all_obs.push(this.roleController.getRole(role.id).pipe(map(
        (data) => {
          if (data && data.statuses && data.statuses.length > 0) {

            data.statuses.forEach((status) => {
              if (status.name === "FILE VALIDATED" && status.destinations.length) {
                const nextRoles = [];
                status.destinations.forEach((dest) => {
                  this.menuBots.forEach((r) => {
                    if (r.id === dest.destinationId) { nextRoles.push(r); }
                  });
                });
                role.nextRoles = nextRoles;
              }
            });
          }
        })));
    });
    this.activemenuBots = _.orderBy(this.activemenuBots, ["sortOrderNumber"]);

    forkJoin(all_obs).subscribe({
      complete: () => {
        this.activemenuBots.forEach((role) => {
          if (!this.isInOrderedmenuBots(role)) {
            this.orderedmenuBots.push(role);
          }
          if (role.nextRoles && Array.isArray(role.nextRoles)) {
            const nextRow = [];
            role.nextRoles.forEach((nr) => {
              if (!this.isInOrderedmenuBots(nr)) {
                nextRow.push(nr);
              }
            });
            if (nextRow.length === 1) {
              this.orderedmenuBots.push(nextRow[0]);
            } else if (nextRow.length > 1) {
              this.orderedmenuBots.push(nextRow);
            }
          }
        });
      },
    });
  }

  private isInOrderedmenuBots(element) {
    return this.orderedmenuBots.some((omr) => {
      if (Array.isArray(omr)) {
        return omr.some((innerOmr) => {
          return innerOmr.id === element.id;
        });
      }
      return omr.id === element.id;
    });
  }

  public static filterUnnecessaryRoles(roles) {
    // remove applicant Guide form
    return roles.filter((role) => role.shortName !== "AP");
  }

  public static filterFEBotRoles(roles) {
    // remove applicant Guide form
    return roles.filter((role) => role.isFrontendBot);
  }

  public deleteRole(role) {
    this.roleController.deleteRole(role.id).subscribe(
      (data) => {
        this.responseSuccessful("Bot deleted!");
        this.ngOnInit();
        this.router.navigate(["/services/" + this.serviceId + "/bots"]);
      },
      (error) => this.showErrorMessage(error),
    );
  }

  public deleteBot(bot) {
    this.botController.deleteBot(bot.id).subscribe(
      (data) => {
        this.responseSuccessful("Bot deleted!");
        this.ngOnInit();
        this.router.navigate(["/services/" + this.serviceId + "/bots"]);
      },
      (error) => this.showErrorMessage(error),
    );
  }
  public openNewRoleLayer() {
    this.showBotAdd = true;
  }

  public closeLayer() {
    this.showBotAdd = false;
  }

  public searchTextChanged(value) {
    this.searchValue = value;
    this.customFilterBy = value;
    this.customFilter.name = value;
    this.botNameField = value;
  }

  public resetSearch() {
    this.searchValue = "";
    this.customFilterBy = "";
    this.customFilter.name = "";
    this.botNameField = "";
  }

  public onBotChange(botName: string): void {
    const bot = this.bots.find((b) => b.name === botName);
    if (bot) {
      this.resetSearch();
      this.router.navigate(["/services/" + this.serviceId + "/bots/" + bot.id]);
    }
  }

  public copyBot(id: any): void {
    const copy: Bot = this.bots.filter((b) => b.id === id)[0];
    if (copy) {
      this.botController.cloneBot(copy).subscribe(
        (data) => {
          this.responseSuccessful("Bot cloned!");
          this.ngOnInit();
        },
        (error) => {
          this.showErrorMessage(error);
        },
      );
    }
  }

  public backFromCVFMapping() {
    this.showCVFMapping = false;
    this.hideList = false;
  }
}
